import axios from "axios";
import { errorHandler } from "../utils/errorHandling";
import { captureApiError, captureNetworkError } from "../utils/sentry";

// Extend the Axios config type to include our metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      requestId: string;
      startTime: number;
    };
  }
}

const baseURL = import.meta.env.VITE_API_BASE_URL;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add request metadata for tracking
    config.metadata = {
      requestId: Math.random().toString(36).substr(2, 9),
      startTime: Date.now(),
    };
    
    // Headers are now added by the useAuthenticatedApi hook for authenticated requests
    return config;
  },
  (error) => {
    errorHandler.logApiError(error, { phase: 'request' });
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful requests in development
    if (import.meta.env.DEV && response.config.metadata) {
      const { requestId, startTime } = response.config.metadata;
      const duration = Date.now() - startTime;
      console.log(`✅ API Request ${requestId}: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`);
    }
    return response;
  },
  (error) => {
    const requestMetadata = error.config?.metadata || {};
    
    if (axios.isCancel(error)) {
      // The request was canceled
      errorHandler.logApiError(error, { 
        ...requestMetadata,
        phase: 'canceled',
        type: 'request_canceled'
      });
      return Promise.reject(new Error("Request canceled"));
    } else if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      
      // Log API error with context
      const errorMeta = {
        ...requestMetadata,
        phase: 'response',
        status: error.response.status,
        statusText: error.response.statusText,
        endpoint: error.config?.url,
        method: error.config?.method,
        responseData: error.response.data,
        duration: requestMetadata.startTime ? Date.now() - requestMetadata.startTime : undefined,
      };
      
      errorHandler.logApiError(error, errorMeta);
      
      // Capture 5xx errors in Sentry (server errors)
      if (error.response.status >= 500) {
        captureApiError(error, errorMeta);
      }
      
      // Prefer the backend's standard error format: { success: false, error: { message: string, ... } }
      let errorMessage =
        error.response.data?.error?.message ||
        error.response.data?.message ||
        "An error occurred";

      // Convert common Postgres duplicate-key error into a friendlier message
      const duplicateKeyRegex =
        /duplicate key value violates unique constraint/;
      let duplicate = false;
      if (duplicateKeyRegex.test(errorMessage)) {
        errorMessage = "A record with the same value already exists.";
        duplicate = true;
      }

      const enhancedError = new Error(errorMessage) as Error & {
        response?: typeof error.response;
        code?: string;
      };
      if (duplicate) {
        enhancedError.code = "DUPLICATE";
      }
      // Preserve the original response for status code checking
      enhancedError.response = error.response;
      return Promise.reject(enhancedError);
    } else if (error.request) {
      // The request was made but no response was received
      const networkMeta = {
        ...requestMetadata,
        phase: 'network',
        type: 'no_response',
        endpoint: error.config?.url,
        method: error.config?.method,
      };
      
      errorHandler.logNetworkError(error, networkMeta);
      captureNetworkError(error, networkMeta);
      return Promise.reject(new Error("No response received from server"));
    } else {
      // Something happened in setting up the request that triggered an Error
      const setupMeta = {
        ...requestMetadata,
        phase: 'setup',
        type: 'request_setup_error',
      };
      
      errorHandler.logApiError(error, setupMeta);
      captureApiError(error, setupMeta);
      return Promise.reject(new Error("Error setting up request"));
    }
  }
);

export const apiClient = axiosInstance;
